import { apiClient } from "@/lib/api";
import type { LoginResponse, LoginSuccess, User } from "@/types/auth";

/**
 * Authenticates a user and returns JWT tokens + user data.
 * POST /api/v1/auth/login/
 */
export async function login(
  email: string,
  password: string,
): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>("/v1/auth/login/", {
    email,
    password,
  });
  return response.data;
}

/**
 * Completes an MFA-gated login by exchanging the interim `mfa_token` and a
 * TOTP or recovery code for real auth tokens + the user identity.
 * POST /api/v1/auth/mfa/verify/
 *
 * The `mfa_token` is a short-lived interim credential from the login challenge —
 * it is NOT a real auth token and must never be persisted as one.
 */
export async function verifyMfa(
  mfa_token: string,
  code: string,
): Promise<LoginSuccess> {
  const response = await apiClient.post<LoginSuccess>("/v1/auth/mfa/verify/", {
    mfa_token,
    code,
  });
  return response.data;
}

/**
 * Logs out the current user.
 * POST /api/v1/auth/logout/
 */
export async function logout(): Promise<void> {
  await apiClient.post("/v1/auth/logout/");
}

/**
 * Mints the short-lived `docs_access` cookie that gates the documentation
 * portal at `/docs/` (TASK-4770).
 * POST /api/v1/auth/docs-session/ -> 204
 *
 * Callable by any signed-in user of any role. The cookie is HttpOnly and
 * scoped to `Path=/docs/`, so JS can never read it — `apiClient` sends
 * `withCredentials: true`, which is what lets the `Set-Cookie` stick.
 *
 * Minted lazily, on opening the docs, rather than at login: the cookie lives
 * an hour and should only be issued to users who actually read the docs.
 */
export async function mintDocsSession(): Promise<void> {
  await apiClient.post("/v1/auth/docs-session/");
}

/**
 * Clears the `docs_access` cookie (TASK-4770).
 * DELETE /api/v1/auth/docs-session/clear/ -> 204
 *
 * Client-side hygiene only, called on logout. It is NOT the security boundary:
 * the cookie carries the caller's session id, so revoking the session already
 * revokes docs access server-side whether or not this ever runs.
 */
export async function clearDocsSession(): Promise<void> {
  await apiClient.delete("/v1/auth/docs-session/clear/");
}

/**
 * Refreshes the access token using a refresh token.
 * POST /api/v1/auth/refresh/
 *
 * With ROTATE_REFRESH_TOKENS disabled the backend returns only `access`,
 * but `refresh` is typed optional so a rotated refresh token is captured
 * automatically should rotation ever be enabled.
 */
export async function refreshToken(
  refresh: string,
): Promise<{ access: string; refresh?: string }> {
  const response = await apiClient.post<{ access: string; refresh?: string }>(
    "/v1/auth/refresh/",
    { refresh },
  );
  return response.data;
}

/**
 * Fetches the currently authenticated user's profile.
 * GET /api/v1/auth/me/
 */
export async function getMe(): Promise<User> {
  const response = await apiClient.get<User>("/v1/auth/me/");
  return response.data;
}

/**
 * Sends a password reset link to the given email address.
 * POST /api/v1/auth/password-reset/
 */
export async function requestPasswordReset(
  email: string,
): Promise<{ detail: string }> {
  const response = await apiClient.post<{ detail: string }>(
    "/v1/auth/password-reset/",
    { email },
  );
  return response.data;
}

/**
 * Confirms a password reset using the token from the reset link.
 * POST /api/v1/auth/password-reset/confirm/
 */
export async function confirmPasswordReset(
  token: string,
  password: string,
): Promise<{ detail: string }> {
  const response = await apiClient.post<{ detail: string }>(
    "/v1/auth/password-reset/confirm/",
    { token, password },
  );
  return response.data;
}

/**
 * Updates the current user's profile (first_name, last_name, avatar_url).
 * PATCH /api/v1/auth/me/
 */
export async function updateMe(data: {
  first_name?: string;
  last_name?: string;
  avatar_url?: string | null;
}): Promise<User> {
  const response = await apiClient.patch<User>("/v1/auth/me/", data);
  return response.data;
}

/**
 * Uploads a user avatar through the backend proxy. The file is POSTed as
 * multipart/form-data to our own origin; the backend streams it to S3-compatible
 * storage and returns the public `file_url`. Replaces the old presigned-URL +
 * direct browser→S3 PUT flow (BUG-376). The caller then PATCHes `auth/me/` with
 * `avatar_url: file_url` to persist it on the user.
 *
 * POST /api/v1/users/avatar-upload/ (multipart, field `file`)
 */
export async function uploadAvatar(file: File): Promise<{ file_url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await apiClient.post<{ file_url: string }>(
    "/v1/users/avatar-upload/",
    formData,
  );
  return response.data;
}

/**
 * Sets a password for an invited user using the invitation token.
 * POST /api/v1/auth/set-password/
 */
export async function setPassword(
  token: string,
  password: string,
): Promise<User> {
  const response = await apiClient.post<User>("/v1/auth/set-password/", {
    token,
    password,
  });
  return response.data;
}
